import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Container,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
} from '@mui/material';
import { 
  Assessment as ReportsIcon,
  GetApp as DownloadIcon,
  Settings as ManageIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

export const Reports = () => {
  const handleGetFreeReport = () => {
    // TODO: Implement get free report functionality
    console.log('Getting free report...');
  };

  const reportFeatures = [
    'Get an overview of driving and history details that matter, such as: Operating radius, where you have driven, U.S. entry details, and more.',
    'Driving licence details',
    'Highlight your skills with complete education and certification records.',
    'Thorough work history records to detail where you have worked, points of contact, vehicles you have driven, and more.',
    'Vehicle photos, certifications, insurance documents, and all other important files you have added to your DriverVerified Profile are included in your report.',
    'Accountability documents like MVR, medical, drug testing, and more.',
    'Unique owner-operator fields to detail your business.',
    'Your work vehicle profiles and photos',
    'Work vehicle insurance history',
    'On-site accountability document support'
  ];

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="/services" 
          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Services
        </Link>
        <Typography color="text.primary">DriverVerified Standard Report</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          DriverVerified Standard Report
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create an easy-to-share report for insurance brokers and employment opportunities to highlight your work experience, education, skills, and more.
        </Typography>
      </Box>

      {/* Overview */}
      <Card sx={{ mb: 4, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Overview
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7 }}>
            Getting insurance or finding employment in the driving industry can be complicated. Save time and avoid 
            the hassle of manually providing your career history, and download a comprehensive, ready-to-go report 
            that is automatically generated using your DriverVerified Profile.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Report contents
          </Typography>
          
          <List sx={{ mb: 4 }}>
            {reportFeatures.map((feature, index) => (
              <ListItem key={index} sx={{ alignItems: 'flex-start', px: 0 }}>
                <ListItemIcon sx={{ mt: 0.5 }}>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={feature} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Features and Benefits */}
      <Card sx={{ mb: 4, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Features and benefits
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            Get instant, real-time report generation and updates. This report session allows you to 
            see what your report looks like and make any adjustments if you felt it is needed.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            As active user, your report is available for 90 days. The archived report is an up-to-date version 
            of your report that is automatically generated when your report session expires.
          </Typography>
        </CardContent>
      </Card>

      {/* Free Report Section */}
      <Card sx={{ mb: 4, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)', border: '2px solid #1976d2' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Your first DriverVerified Report is free!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            Seamlessly combine your work history, experience, company details, and relevant files into a DriverVerified 
            Report. Give it a try and see for yourself.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleGetFreeReport}
            startIcon={<DownloadIcon />}
            sx={{
              backgroundColor: '#4ecdc4',
              '&:hover': { backgroundColor: '#45b7d1' },
              px: 4,
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Get free report
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 2, fontStyle: 'italic' }}>
            *You must complete your DriverVerified Profile to create your free report.
          </Typography>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card sx={{ boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
            Pricing
          </Typography>
          
          <Box sx={{ textAlign: 'center', p: 4, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              One Report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              An affordable "pay as you go" option for businesses that occasionally need a report.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Includes one report with 7-day access for fine-tune and download.
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
              $15.00 FOR ONE USER
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
              ADDITIONAL USERS $2.50 EACH
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
              Your first report is free!
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={handleGetFreeReport}
              sx={{
                backgroundColor: '#4ecdc4',
                '&:hover': { backgroundColor: '#45b7d1' },
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Get free report
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}; 